import { useState } from "react";
import { useRouter } from "expo-router";
import { useAtomValue, useSetAtom } from "jotai/react";
import { toast } from "sonner-native";

import { ApplicationScreenLayout } from "@/blocks/ApplicationScreenLayout";
import { BrokerConsentAnswerButtons } from "@/blocks/BrokerConsentAnswerButtons";
import { BrokerConsentDeclineCard } from "@/blocks/BrokerConsentDeclineCard";
import { ErrorFallback } from "@/blocks/ErrorFallback";
import { VnBox } from "@/components/VnBox";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { useCreatePatientConsentMutation } from "@/data/mutations/consentsMutations";
import { useUpdateCurrentPatientMutation } from "@/data/mutations/patientsMutations";
import { useListApplicationsQuery } from "@/data/queries/applicationsQueries";
import { useBrokerQuery } from "@/data/queries/brokersQueries";
import {
  BrokerConsentAnswerType,
  getBrokerConsentCopy,
} from "@/domain/brokerConsent";
import { useTranslator } from "@/hooks/useTranslate";
import { I18nKeyType } from "@/i18n";
import {
  applicationCurrentStepAtom,
  applicationTotalStepsAtom,
} from "@/stores/atoms";
import { logger } from "@/utils/logger";

type BrokerType = NonNullable<ReturnType<typeof useBrokerQuery>["data"]>;

const getSubtitleI18nKey = (
  broker: BrokerType | null | undefined,
): I18nKeyType => {
  if (!broker) {
    return "native:brokerConsentScreen.subtitle";
  }

  switch (broker.type) {
    case "INDIVIDUAL":
      return broker.licenseNumber
        ? "native:brokerConsentScreen.subtitleWithIndividualLicense"
        : "native:brokerConsentScreen.subtitleWithIndividual";
    case "ORGANIZATION":
      return broker.licenseNumber
        ? "native:brokerConsentScreen.subtitleWithOrganizationLicense"
        : "native:brokerConsentScreen.subtitleWithOrganization";
  }
};

export const ApplicationBrokerConsentScreen = () => {
  const router = useRouter();
  const { t } = useTranslator();
  const setApplicationCurrentStep = useSetAtom(applicationCurrentStepAtom);
  const applicationTotalSteps = useAtomValue(applicationTotalStepsAtom);
  const { mutateAsync: createPatientConsent } =
    useCreatePatientConsentMutation();
  const { mutateAsync: updateCurrentPatient } =
    useUpdateCurrentPatientMutation();
  const { data: broker } = useBrokerQuery();
  const {
    data: draftApplications,
    error: draftApplicationsError,
    isSuccess: isDraftApplicationsSuccess,
    isFetching: isFetchingDraftApplications,
    isFetchedAfterMount: isDraftApplicationsFetchedAfterMount,
    refetch: refetchDraftApplications,
  } = useListApplicationsQuery({ filters: { status: "DRAFT" } });
  const [selectedAnswer, setSelectedAnswer] =
    useState<BrokerConsentAnswerType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // A warm cache is served synchronously and may describe a different set of drafts.
  const isAwaitingFreshDraftApplications =
    isFetchingDraftApplications && !isDraftApplicationsFetchedAfterMount;

  // Failed refetches retain cached drafts and count as fetched after mount.
  const hasFreshDraftApplications =
    isDraftApplicationsFetchedAfterMount && isDraftApplicationsSuccess;

  const brokerConsentCopy = hasFreshDraftApplications
    ? getBrokerConsentCopy(
        draftApplications?.map(application => application.applicationType) ??
          [],
      )
    : null;

  const submitConsent = async (isConsented: boolean) => {
    setIsSubmitting(true);
    try {
      await createPatientConsent({
        consentType: "BROKER_HANDOFF",
        isConsented,
      });

      await updateCurrentPatient({
        patientData: { applicationLastStep: "BROKER_CONSENT" },
      });

      setApplicationCurrentStep(step =>
        Math.min(step + 1, applicationTotalSteps),
      );

      router.navigate({
        pathname: "/(authed)/(modal)/application/citizenship-status",
      });
    } catch (error) {
      logger.error({ message: "Failed to record the broker consent", error });
      toast.error(t("common:errors.unknown"));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Declining only arms Continue, so the alternatives are seen before committing.
  const handleAnswerPress = (answer: BrokerConsentAnswerType) => {
    setSelectedAnswer(answer);

    if (answer === "yes") {
      void submitConsent(true);
    }
  };

  const isConnectingAgent = isSubmitting && selectedAnswer === "yes";

  const subtitleI18nKey = getSubtitleI18nKey(
    brokerConsentCopy ? broker : undefined,
  );

  const brokerAttributionI18nOptions = broker
    ? {
        brokerName: broker.name,
        ...(broker.licenseNumber && { licenseNumber: broker.licenseNumber }),
      }
    : undefined;

  return (
    <ApplicationScreenLayout
      shouldShowNextButton={selectedAnswer === "no"}
      shouldShowBackButton={false}
      nextButtonI18nKey={brokerConsentCopy?.declineContinueButtonI18nKey}
      isNextButtonLoading={isSubmitting && selectedAnswer === "no"}
      onNextButtonPress={() => {
        void submitConsent(false);
      }}
      titleI18nKey={
        brokerConsentCopy ? "native:brokerConsentScreen.title" : undefined
      }
      descriptionI18nKey={subtitleI18nKey}
      descriptionI18nOptions={brokerAttributionI18nOptions}>
      {brokerConsentCopy ? (
        <VnBox className="gap-6">
          <BrokerConsentAnswerButtons
            selectedAnswer={selectedAnswer}
            isLoading={isConnectingAgent}
            isDisabled={isSubmitting}
            onAnswerPress={handleAnswerPress}
          />
          {selectedAnswer === "no" && (
            <BrokerConsentDeclineCard
              agentAssistedPrograms={brokerConsentCopy.agentAssistedPrograms}
              fallbackI18nKey={brokerConsentCopy.declineFallbackI18nKey}
            />
          )}
        </VnBox>
      ) : isAwaitingFreshDraftApplications ? (
        <VnLoadingSpinner />
      ) : (
        // The back button is hidden here, so a retry is the only way out.
        <ErrorFallback
          error={draftApplicationsError}
          resetError={async () => {
            await refetchDraftApplications();
          }}
        />
      )}
    </ApplicationScreenLayout>
  );
};
